import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import StraightenIcon from '@mui/icons-material/Straighten';
import { type SxProps, type Theme } from '@mui/material';
import { ActionButton, RhfSelect } from '@core/components';
import { useDebouncedValue } from '@core/hooks/useDebouncedValue';
import { useInfinitePage } from '@core/hooks/useInfinitePage';
import {
  type Customer,
  type CustomerPageParams,
  type CustomerQuery,
  customerKeys,
  getCustomersPage,
} from '../../api/customer';
import { useCreateActionOption } from '../hooks/useCreateActionOption';
import { useCustomerById } from '../hooks/useCustomerById';
import { getFullName } from '../utils/customer';
import CustomerDialog from './CustomerDialog';
import CustomerMeasurementsDrawer from './CustomerMeasurementsDrawer';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 250;

type RhfCustomerSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  showMeasurements?: boolean;
  sx?: SxProps<Theme>;
};

export function RhfCustomerSelect<T extends FieldValues>({
  name,
  label,
  required,
  disabled,
  showMeasurements = false,
  sx,
}: RhfCustomerSelectProps<T>) {
  const queryClient = useQueryClient();
  const { watch } = useFormContext<T>();
  const selectedId = String(watch(name) ?? '');
  const [query, setQuery] = useState('');
  const [measurementsOpen, setMeasurementsOpen] = useState(false);
  const search = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const customerQuery = useMemo<CustomerQuery>(
    () => (search ? { search } : {}),
    [search]
  );
  const customerAction = useCreateActionOption<Customer>(
    'Add new customer…',
    (props) => <CustomerDialog {...props} />
  );

  const fetchCustomersPage = useCallback(
    async (params: CustomerPageParams) => {
      const page = await getCustomersPage(params);
      for (const customer of page.items) {
        queryClient.setQueryData(customerKeys.byId(customer.id), customer);
      }
      return page;
    },
    [queryClient]
  );
  const {
    items: loadedCustomers,
    isLoading,
    loadMore: loadMoreCustomers,
  } = useInfinitePage({
    queryKey: customerKeys.page(customerQuery),
    query: customerQuery,
    pageSize: PAGE_SIZE,
    fetchPage: fetchCustomersPage,
  });
  const parsedId = Number(selectedId);
  const hasSelectedId = selectedId !== '' && Number.isInteger(parsedId);
  const selectedFromPage = hasSelectedId
    ? loadedCustomers.find((customer) => String(customer.id) === selectedId)
    : undefined;
  const selectedCustomerQuery = useCustomerById(
    hasSelectedId && !selectedFromPage ? parsedId : null
  );
  const selectedCustomer = selectedFromPage ?? selectedCustomerQuery.data;
  const customers = useMemo(() => {
    if (!selectedCustomer || selectedFromPage) {
      return loadedCustomers;
    }
    return [selectedCustomer, ...loadedCustomers];
  }, [loadedCustomers, selectedCustomer, selectedFromPage]);

  return (
    <>
      <RhfSelect<T, Customer, Customer>
        name={name}
        label={label}
        items={customers}
        getOptionValue={(item) => String(item.id)}
        getOptionLabel={getFullName}
        required={required}
        disabled={disabled}
        disableClearable
        loading={isLoading}
        skipLocalFilter
        sx={sx}
        actionOption={customerAction}
        onQueryChange={setQuery}
        onListboxScrollEnd={loadMoreCustomers}
        endAdornment={
          showMeasurements ? (
            <ActionButton
              variant="subtle"
              size="xs"
              disabled={!selectedCustomer}
              icon={<StraightenIcon sx={{ fontSize: 16 }} />}
              onClick={(event) => {
                event.stopPropagation();
                setMeasurementsOpen(true);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              sx={{
                mr: '-2px',
                pointerEvents: 'auto',
                fontWeight: 500,
                '&:hover': { borderColor: 'divider' },
              }}
            >
              View Measurements
            </ActionButton>
          ) : undefined
        }
      />
      {showMeasurements ? (
        <CustomerMeasurementsDrawer
          open={measurementsOpen}
          customer={selectedCustomer}
          onClose={() => setMeasurementsOpen(false)}
        />
      ) : null}
    </>
  );
}
